import { useEffect, useRef } from 'react';

import { useExtensionContext } from '$src/context/ExtensionContext';
import { useCart, useShop } from '$src/hooks/queries';
import { useROEmailAtom } from '$src/hooks/state';

import { useActionExecutor } from './useActionExecutor';
import { useExtensionManager } from './useExtensionManager';
import { useExtensions } from './useExtensions';

export const useExtensionCartWatcher = () => {
  const { data: cart } = useCart();
  const { data: shop } = useShop();
  const email = useROEmailAtom();

  const { addAttributes, trackProducts, removeProducts } = useExtensionContext();
  const extensions = useExtensions();
  const { executeActions: getExtensionActions } = useExtensionManager(extensions);
  const { executeActions: performActions } = useActionExecutor();

  const lastProcessedDiscountCodes = useRef<string[]>([]);

  useEffect(() => {
    if (!cart || !shop) return;

    const currentDiscountCodes = (cart.discounts?.map((d) => d.code).filter(Boolean) as string[]) || [];
    const previousDiscountCodes = lastProcessedDiscountCodes.current;

    const newDiscountCodes = currentDiscountCodes.filter((code) => !previousDiscountCodes.includes(code));
    const removedDiscountCodes = previousDiscountCodes.filter((code) => !currentDiscountCodes.includes(code));

    // Handle removed discount codes - remove products automatically
    const currentLineItemIds = cart.lineItems?.map((item) => item.id) || [];
    for (const removedCode of removedDiscountCodes) {
      console.warn('[CartWatcher] Discount code removed:', removedCode);
      removeProducts(removedCode, currentLineItemIds);
    }

    // Handle new discount codes - ULTRA SIMPLIFIED
    for (const discountCode of newDiscountCodes) {
      console.warn('[CartWatcher] New discount code detected:', discountCode);

      // Execute discount application actions
      const actionsToExecute = getExtensionActions('on-discount-application', {
        cartId: cart.id,
        shopId: shop.id,
        email: email || cart.customer?.email,
        discountCode,
      });

      if (actionsToExecute.length > 0) {
        console.warn('[CartWatcher] Executing actions for discount:', discountCode, actionsToExecute);

        performActions(actionsToExecute)
          .then((results: any) => {
            console.warn('[CartWatcher] Actions executed for discount:', discountCode, results);

            // Store custom attributes in context
            if (results.customAttributes.length > 0) {
              addAttributes(results.customAttributes);
            }

            // Track products added by this discount for future removal
            const addedLineItemIds = results.addedProducts
              .map((p: any) => p.lineItemId)
              .filter(Boolean) as string[];
            if (addedLineItemIds.length > 0) {
              trackProducts(discountCode, addedLineItemIds);
            }
          })
          .catch((error) => {
            console.error('[CartWatcher] Failed to execute actions for discount:', discountCode, error);
          });
      }
    }

    // Update last processed codes
    lastProcessedDiscountCodes.current = [...currentDiscountCodes];
  }, [cart, shop, email, getExtensionActions, performActions, addAttributes, trackProducts, removeProducts]);

  // Return nothing - this is a pure side-effect hook
  return null;
};
